// Form select with an animated menu. Submits through a hidden input, so it works
// inside a plain <form> + FormData exactly like a native <select name=…>.
import { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
}

export const SelectMenu = ({
  name,
  options,
  defaultValue = "",
  placeholder = "Select",
  label,
}: {
  name: string;
  options: SelectOption[];
  defaultValue?: string;
  placeholder?: string;
  label: string;
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [active, setActive] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (v: string) => {
    setValue(v);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") return setOpen(false);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return setOpen(true);
      setActive((a) => (a + (e.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
    }
    if ((e.key === "Enter" || e.key === " ") && open && active >= 0) {
      e.preventDefault();
      pick(options[active].value);
    }
  };

  return (
    <div ref={ref} className="relative mt-1.5" onKeyDown={onKey}>
      <input type="hidden" name={name} value={value} />
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-11 w-full items-center justify-between rounded-lg border bg-background px-3.5 text-left text-[15px] font-normal outline-none transition-[border-color,box-shadow] duration-150 ${
          open ? "border-foreground shadow-[0_0_0_3px_hsl(0_0%_0%/0.06)]" : "border-border hover:border-[hsl(0_0%_72%)]"
        }`}
      >
        <span className={current ? "text-foreground" : "text-muted-foreground"}>{current?.label ?? placeholder}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.18 }}>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            id={id}
            role="listbox"
            aria-label={label}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 top-full z-30 mt-1.5 origin-top rounded-xl border border-border bg-popover p-1.5 shadow-[0_16px_40px_-16px_hsl(0_0%_0%/0.25)]"
          >
            {options.map((o, i) => (
              <motion.li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.02 * i, duration: 0.14 }}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o.value)}
                className={`flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-[15px] font-normal text-foreground ${i === active ? "bg-muted" : ""}`}
              >
                {o.label}
                {o.value === value && <Check className="h-4 w-4" />}
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
